import { useCallback, useEffect, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toMinorUnits } from "@/utils/helpers";
import { useTranslation, Trans } from "react-i18next";
import { BankTransferDetailList } from "@/components/bank-transfer/bank-transfer-details/BankTransferDetailList";
import { PrinterFriendlyBankTransferDetailList } from "@/components/bank-transfer/bank-transfer-details/PrinterFriendlyBankTransferDetailList";
import { GridPageLayout } from "@/components/general/grid-page-layout/GridPageLayout";
import { NavButtons } from "@/components/general/nav-buttons/NavButtons";
import {
  Section,
  SectionHeader,
  SectionTitle,
  SectionSubtitle,
} from "@/components/general/section";
import { useMethod } from "@/hooks/deposit/useMethod";
import { useAmountForm } from "@/hooks/deposit/useAmountForm";
import { useCurrencies } from "@/hooks/deposit/useCurrencies";
import { useAccount } from "@/hooks/deposit/useAccount";
import { useFormatAmount } from "@/hooks/useFormatAmount";
import { useUserData } from "@/hooks/useUserData";
import { ERoutes } from "@/utils/enums";
import { Typo, Button } from "@pepperstone/pepper-ui-kit";

// Pure function - moved outside component to avoid dependency issues
const createBankTransferItems = (
  bankDetails: {
    bankName: string;
    bankAddress: string;
    swiftCode: string;
    accountName: string;
    sortCode?: string;
  },
  uniqueDetails: {
    currency: string;
    accountNumber: string;
    iban: string;
  },
  referenceCode: string,
  amount: string,
  estimatedArrival?: string
) => {
  // Create object in the correct order to match the image
  const items: Record<string, string> = {
    account_name: bankDetails.accountName,
    account_number: uniqueDetails.accountNumber,
    bank_name: bankDetails.bankName,
    currency: uniqueDetails.currency,
    amount: amount,
    reference_code: referenceCode,
    iban: uniqueDetails.iban,
    swift_code: bankDetails.swiftCode,
  };

  // Add optional fields if they exist
  if (bankDetails.sortCode) {
    items.sort_code = bankDetails.sortCode;
  }
  if (estimatedArrival) {
    items.estimated_arrival = estimatedArrival;
  }

  return items;
};

export const BankTransfer = () => {
  const navigate = useNavigate();
  const { accountLoginId } = useParams<{ accountLoginId: string }>();
  const { selectedPaymentMethod } = useMethod();
  const { amountFormData } = useAmountForm();
  const { currencies } = useCurrencies();
  const { selectedAccount } = useAccount();
  const { data: userData } = useUserData();
  const { formatAmount } = useFormatAmount();
  const { t } = useTranslation();

  // Define callbacks before early return to satisfy hooks rules
  const handleBack = useCallback(() => {
    if (accountLoginId) {
      navigate(`../${accountLoginId}/${ERoutes.ChooseMethod}`);
    } else {
      navigate(`../${ERoutes.ChooseMethod}`);
    }
  }, [navigate, accountLoginId]);

  const handleIvePaid = useCallback(() => {
    if (!selectedAccount) return;
    navigate(`../${ERoutes.Completion}`, {
      state: {
        bankTransfer: {
          account: selectedAccount,
          money: {
            amount: String(
              toMinorUnits(
                amountFormData?.tradingValue ?? 0,
                selectedAccount.currency.decimals
              )
            ),
            decimals: selectedAccount.currency.decimals,
            code: selectedAccount.currency.code,
          },
        },
      },
    });
  }, [navigate, selectedAccount, amountFormData]);

  const handlePrint = useCallback(() => {
    const originalTitle = document.title;
    const printModeClassName = "cashier-pdf-print-mode";
    const supportsAfterPrint = "onafterprint" in window;
    let isTitleRestored = false;

    const restoreTitle = () => {
      if (isTitleRestored) {
        return;
      }

      isTitleRestored = true;
      document.title = originalTitle;
      document.body.classList.remove(printModeClassName);
      window.removeEventListener("afterprint", restoreTitle);
    };

    document.body.classList.add(printModeClassName);
    document.title = "Pepperstone payment details";

    if (supportsAfterPrint) {
      window.addEventListener("afterprint", restoreTitle);
    }

    try {
      window.print();
    } catch (error) {
      restoreTitle();
      throw error;
    } finally {
      if (!supportsAfterPrint) {
        restoreTitle();
      }
    }
  }, []);

  // Redirect to accounts page if required data is missing
  useEffect(() => {
    if (
      !selectedAccount ||
      !selectedPaymentMethod ||
      !amountFormData?.selectedProcessingCurrency
    ) {
      navigate(`../${ERoutes.Accounts}`);
    }
  }, [selectedAccount, selectedPaymentMethod, amountFormData, navigate]);

  // Get PSP settings based on current license
  const pspSettings = useMemo(() => {
    if (!selectedPaymentMethod || !userData) return null;

    const licenseId = userData.license_id;
    const settings = selectedPaymentMethod.psp_settings;

    // Try to get license-specific settings, fallback to "default"
    return settings[licenseId] || settings["default"] || null;
  }, [selectedPaymentMethod, userData]);

  // Get bank details from PSP settings
  const bankDetails = useMemo(() => {
    if (!pspSettings?.bank_details) return null;

    const bank = pspSettings.bank_details;
    return {
      bankName: bank.bank_name || "",
      bankAddress: bank.bank_address || "",
      swiftCode: bank.swift_code || "",
      accountName: bank.account_name || "",
      sortCode: bank.sort_code,
    };
  }, [pspSettings]);

  // Get account details matching the processing currency
  const uniqueDetails = useMemo(() => {
    if (
      !pspSettings?.account_details ||
      !amountFormData?.selectedProcessingCurrency ||
      !currencies
    )
      return null;

    const processingCurrencyCode =
      currencies[amountFormData.selectedProcessingCurrency.code]?.code;
    if (!processingCurrencyCode) return null;

    // Find account detail that matches the processing currency
    const accountDetail = pspSettings.account_details.find((detail) =>
      detail.currencies.includes(processingCurrencyCode)
    );

    if (!accountDetail) return null;

    return {
      currency: processingCurrencyCode,
      accountNumber: accountDetail.account_number,
      iban: accountDetail.iban || "",
    };
  }, [pspSettings, amountFormData, currencies]);

  // Get reference code from selected account ID
  const referenceCode = useMemo(() => {
    return selectedAccount?.accountLoginId || "";
  }, [selectedAccount]);

  // Format amount for display
  const formattedAmount = useMemo(() => {
    if (
      !amountFormData?.processingValue ||
      !amountFormData?.selectedProcessingCurrency ||
      !currencies
    ) {
      return "";
    }
    const currency = currencies[amountFormData.selectedProcessingCurrency.code];
    if (!currency) return "";
    return formatAmount(amountFormData.processingValue, currency);
  }, [amountFormData, currencies, formatAmount]);

  // Get estimated arrival time from payment method settings
  const estimatedArrival = useMemo(() => {
    if (!selectedPaymentMethod?.base_settings) return undefined;
    // Check if it's DepositBaseSettings (has 'deposit' property)
    if ("deposit" in selectedPaymentMethod.base_settings) {
      return selectedPaymentMethod.base_settings.deposit
        ?.expected_processing_time;
    }
    return undefined;
  }, [selectedPaymentMethod]);

  // Create items only if all required data is available
  const items = useMemo(() => {
    if (!bankDetails || !uniqueDetails || !referenceCode || !formattedAmount) {
      // Return empty object as fallback to satisfy type requirements
      return {} as Record<string, string>;
    }

    return createBankTransferItems(
      bankDetails,
      uniqueDetails,
      referenceCode,
      formattedAmount,
      estimatedArrival
    );
  }, [
    bankDetails,
    uniqueDetails,
    referenceCode,
    formattedAmount,
    estimatedArrival,
  ]);

  // Show loading or error state if data is not available
  // Note: Redirect is handled in useEffect above, but we still need to guard the render
  if (
    !selectedPaymentMethod ||
    !userData ||
    !selectedAccount ||
    !items ||
    Object.keys(items).length === 0
  ) {
    return null;
  }

  return (
    <GridPageLayout>
      <Section>
        <SectionHeader>
          <div>
            <SectionTitle>
              {t("deposit.payment_placement.bank_transfer.title")}
            </SectionTitle>
            <Typo elem="p" type="body" size="md">
              <Trans
                i18nKey="deposit.payment_placement.bank_transfer.instructions_description"
                components={{ bold: <strong /> }}
              />
            </Typo>
          </div>
        </SectionHeader>
        <SectionHeader>
          <SectionSubtitle>Deposit instructions</SectionSubtitle>
          <Button
            onClick={handlePrint}
            variant="ps-btn-background-outline"
            type="button"
          >
            PDF
          </Button>
        </SectionHeader>
        <BankTransferDetailList items={items} />
        <PrinterFriendlyBankTransferDetailList items={items} />
        <NavButtons
          nextLabel="I've paid"
          nextAction={handleIvePaid}
          backAction={handleBack}
        />
      </Section>
    </GridPageLayout>
  );
};
